import React, { useState } from 'react';
import { Mail, Calendar, Cloud, FileText, RefreshCw, Trash2, AlertCircle, PauseCircle, Play, Square } from 'lucide-react';
import { StatusBadge, type StatusBadgeVariant } from '../../atoms/StatusBadge';
import { Checkbox } from '../../molecules/Checkbox';
import './IntegrationCard.css';

export type IntegrationType = 'email' | 'calendar' | 'cloud' | 'document';
/**
 * #3517: `paused` is the state an operator puts a mailbox in to stop polling without
 * claiming anything is wrong with it. The server has always honoured it — the poller
 * selects only active-or-retryable-error rows — but until this member existed the union
 * had nowhere to put it, so `mapConnectionStatus`'s `default:` arm rendered a
 * deliberately parked mailbox as an **error** in the farmer's UI.
 */
export type IntegrationStatus = 'connected' | 'error' | 'connecting' | 'paused';
export type SyncScheduleMode = 'automatic' | 'daily' | 'weekly';

export interface HistoricalImportProgress {
  /** Whether historical import is in progress */
  inProgress: boolean;
  /** Number of messages pulled so far */
  pulled: number;
  /** Total messages to pull (estimated) */
  total: number;
}

export interface IntegrationCardProps {
  /** Unique identifier for the integration */
  id: string;
  /** Type of integration */
  type: IntegrationType;
  /** Display name for the integration */
  name: string;
  /** Account identifier (e.g., email address) */
  account: string;
  /** Current status */
  status: IntegrationStatus;
  /** ISO timestamp of last sync */
  lastSync?: string;
  /** Number of new messages received in last check (null = unknown, 0 = none) */
  lastCheckNewMessages?: number | null;
  /** Historical import progress (when pulling past messages) */
  historicalImport?: HistoricalImportProgress;
  /** Error message if status is 'error' */
  error?: string | null;
  /** Whether a check is currently in progress */
  isChecking?: boolean;
  /** Current sync schedule mode */
  syncSchedule?: SyncScheduleMode;
  /** Configured sync time (HH:MM) for daily/weekly */
  syncScheduleTime?: string | null;
  /** Configured day of week (0-6) for weekly */
  syncScheduleDay?: number | null;
  /** #1570: whether the email body is stored as a separate PDF during ingest (default off) */
  extractEmailBodyPdf?: boolean;
  /**
   * #3035: the IMAP folders this mailbox polls. Shown as a summary, editable via
   * `onEditFolders` / `onFoldersChange`. Empty means the mailbox polls NOTHING, which is
   * rendered as a warning rather than as an empty list — it is the failure this issue
   * closes, and a blank line would read as "not configured yet".
   */
  pollingFolders?: string[];
  /**
   * #3035: non-fatal degradation codes from the server (`no_polling_folders`,
   * `sent_folder_undiscoverable`). Deliberately NOT folded into `status`: they must not
   * take a working mailbox down, they explain a gap in one.
   */
  statusWarnings?: string[];
  /**
   * #3184: wire name → mUTF-7-decoded display name for `pollingFolders`
   * (`INBOX.Grundh&APY-fer 2022` → `INBOX.Grundhöfer 2022`); only entries whose decode
   * differs are present. Labels only — every value submitted or compared stays the wire
   * name (decode is not injective). Absent degrades to raw names.
   */
  pollingFolderDisplayNames?: Record<string, string>;
  /**
   * #3035: every folder the server offers as a poll target, once fetched. `undefined`
   * means "not fetched yet"; an empty array after a fetch means the enumeration came back
   * empty or failed — see `folderEnumerationError`.
   */
  availableFolders?: string[];
  /**
   * #3184: wire → decoded labels for the folder editor, from the same fetch as
   * `availableFolders`. Covers offered AND persisted names (the server builds it over the
   * union), so a persisted folder the server no longer offers still gets its label.
   */
  folderDisplayNames?: Record<string, string>;
  /** #3035: true while the folder tree is being fetched from the mail server. */
  isLoadingFolders?: boolean;
  /** #3035: German message when the tree could not be listed (the selection is unchanged). */
  folderEnumerationError?: string | null;
  /** Handler for "Check now" action */
  onCheckNow?: (id: string) => void;
  /** Handler for "Reconnect" action */
  onReconnect?: (id: string) => void;
  /** Handler for "Disconnect" action */
  onDisconnect?: (id: string) => void;
  /** Handler for stopping historical import */
  onStopHistoricalImport?: (id: string) => void;
  /** Handler for schedule mode change */
  onScheduleChange?: (id: string, mode: SyncScheduleMode, time?: string, day?: number) => void;
  /** #1570: handler for the extract-email-body-PDF toggle. When omitted, the toggle is hidden. */
  onExtractEmailBodyPdfChange?: (id: string, enabled: boolean) => void;
  /**
   * #3035: called when the farmer opens the folder editor, so the host can fetch the tree
   * (which costs an IMAP connection — hence on demand, never on render). When omitted the
   * folder summary is read-only.
   */
  onEditFolders?: (id: string) => void;
  /** #3035: called with the new selection when the farmer saves it. */
  onFoldersChange?: (id: string, folders: string[]) => void;
  /** Optional custom className */
  className?: string;
  /** Labels for i18n support */
  labels?: IntegrationCardLabels;
}

export interface IntegrationCardLabels {
  checkNow?: string;
  checking?: string;
  reconnect?: string;
  disconnect?: string;
  lastChecked?: string;
  errorPrefix?: string;
  notCollecting?: string;
  noNewMessages?: string;
  newMessagesReceived?: string;
  historicalImportPulled?: string;
  historicalImportOf?: string;
  stopImport?: string;
  stopImportConfirm?: string;
  stopImportYes?: string;
  stopImportNo?: string;
  // Relative time labels
  never?: string;
  justNow?: string;
  minuteAgo?: string;
  minutesAgo?: string;
  hourAgo?: string;
  hoursAgo?: string;
  dayAgo?: string;
  daysAgo?: string;
  connecting?: string;
  /** #3517: badge text for a mailbox an operator has parked */
  paused?: string;
  /** #3517: why it is parked and what happens next — a paused mailbox with no explanation
   *  reads as a broken one, which is the failure this state exists to avoid */
  pausedExplanation?: string;
  /** Locale for date formatting (e.g., 'en-US', 'de-DE') */
  dateLocale?: string;
  // Schedule labels
  scheduleLabel?: string;
  scheduleAutomatic?: string;
  scheduleDaily?: string;
  scheduleWeekly?: string;
  scheduleTimeLabel?: string;
  scheduleDayLabel?: string;
  scheduleDays?: string[];
  /** Description for automatic mode (plain text) */
  scheduleAutomaticDesc?: string;
  /** Description template for daily mode, use {time} placeholder */
  scheduleDailyDesc?: string;
  /** Description template for weekly mode, use {day} and {time} placeholders */
  scheduleWeeklyDesc?: string;
  scheduleEdit?: string;
  scheduleDone?: string;
  /** #1570: label for the extract-email-body-PDF toggle */
  extractEmailBodyPdf?: string;
  /** #3035: label of the polled-folders row */
  foldersLabel?: string;
  /** #3035: "{count} of {total} folders" — the coverage signal that makes this self-diagnosing */
  foldersCoverage?: string;
  /** #3035: warning shown when the mailbox polls nothing at all */
  foldersNoneWarning?: string;
  /** #3545: warning shown when the server refused to let us read one or more folders */
  foldersUnreadableWarning?: string;
  /** #3035: open the folder editor */
  foldersEdit?: string;
  /** #3035: save the edited selection */
  foldersSave?: string;
  /** #3035: abandon the edit */
  foldersCancel?: string;
  /** #3035: shown while the folder tree is being fetched */
  foldersLoading?: string;
  /** #3035: validation message when the farmer unticks everything */
  foldersRequired?: string;
}

const defaultLabels: Required<IntegrationCardLabels> = {
  checkNow: 'Check now',
  checking: 'Checking...',
  reconnect: 'Reconnect',
  disconnect: 'Disconnect',
  lastChecked: 'Last checked',
  errorPrefix: 'Error',
  notCollecting: 'Emails are currently not being collected.',
  noNewMessages: 'No new messages received',
  newMessagesReceived: 'new messages received',
  historicalImportPulled: 'Pulled',
  historicalImportOf: 'of',
  stopImport: 'Stop',
  stopImportConfirm: 'Stop import?',
  stopImportYes: 'Yes',
  stopImportNo: 'No',
  // Relative time labels
  never: 'Never',
  justNow: 'Just now',
  minuteAgo: '1 minute ago',
  minutesAgo: '{count} minutes ago',
  hourAgo: '1 hour ago',
  hoursAgo: '{count} hours ago',
  dayAgo: '1 day ago',
  daysAgo: '{count} days ago',
  connecting: 'Connecting...',
  paused: 'Paused',
  pausedExplanation: 'Email collection is paused by support. No emails are being collected right now.',
  dateLocale: 'en-US',
  // Schedule labels
  scheduleLabel: 'Sync schedule',
  scheduleAutomatic: 'Automatic',
  scheduleDaily: 'Once daily',
  scheduleWeekly: 'Once weekly',
  scheduleTimeLabel: 'Time',
  scheduleDayLabel: 'Day',
  scheduleDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  scheduleAutomaticDesc: 'Frequent checks during business hours, less often in evenings and on weekends.',
  scheduleDailyDesc: 'Every day at {time}.',
  scheduleWeeklyDesc: 'Every {day} at {time}.',
  scheduleEdit: 'Edit',
  scheduleDone: 'Done',
  extractEmailBodyPdf: 'Store email body as a separate PDF',
  foldersLabel: 'Folders checked',
  foldersCoverage: 'Checking {count} of {total} folders',
  foldersNoneWarning: 'No folders are being checked — no documents can arrive from this mailbox.',
  foldersUnreadableWarning:
    'Some folders could not be read — the mail server refused access, so no documents can arrive from them.',
  foldersEdit: 'Edit',
  foldersSave: 'Save',
  foldersCancel: 'Cancel',
  foldersLoading: 'Loading folders...',
  foldersRequired: 'Please select at least one folder.',
};

/** #3035: stable code for "this mailbox polls nothing" — mirrors the server's constant. */
export const NO_POLLING_FOLDERS_WARNING = 'no_polling_folders';

/**
 * #3545: stable code for "the server refused to let us read one or more selected
 * folders" — mirrors the server's constant. The count and the names deliberately do
 * NOT travel on the wire: the farmer already sees their own folder list here, and a
 * mailbox name is customer data that must not reach telemetry (#2674).
 */
export const FOLDERS_UNREADABLE_WARNING = 'folders_unreadable';

/**
 * Formats a date string to a relative time display.
 */
const formatRelativeTime = (dateString: string, labels: Required<IntegrationCardLabels>): string => {
  if (!dateString) return labels.never;

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.round(diffMs / 60000);
  const diffHours = Math.round(diffMs / 3600000);
  const diffDays = Math.round(diffMs / 86400000);

  if (diffMins < 1) return labels.justNow;
  if (diffMins < 60) {
    return diffMins === 1
      ? labels.minuteAgo
      : labels.minutesAgo.replace('{count}', String(diffMins));
  }
  if (diffHours < 24) {
    return diffHours === 1
      ? labels.hourAgo
      : labels.hoursAgo.replace('{count}', String(diffHours));
  }
  if (diffDays < 7) {
    return diffDays === 1
      ? labels.dayAgo
      : labels.daysAgo.replace('{count}', String(diffDays));
  }

  return date.toLocaleDateString(labels.dateLocale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Returns the appropriate icon for the integration type.
 */
const getIntegrationIcon = (type: IntegrationType) => {
  switch (type) {
    case 'email':
      return <Mail size={20} />;
    case 'calendar':
      return <Calendar size={20} />;
    case 'cloud':
      return <Cloud size={20} />;
    case 'document':
      return <FileText size={20} />;
    default:
      return <Mail size={20} />;
  }
};

/**
 * IntegrationCard - Displays an integration with status, actions, and error messages.
 */
export const IntegrationCard: React.FC<IntegrationCardProps> = ({
  id,
  type,
  name,
  account,
  status,
  lastSync,
  lastCheckNewMessages,
  historicalImport,
  error,
  isChecking = false,
  syncSchedule,
  syncScheduleTime,
  syncScheduleDay,
  extractEmailBodyPdf = false,
  pollingFolders,
  pollingFolderDisplayNames,
  statusWarnings,
  availableFolders,
  folderDisplayNames,
  isLoadingFolders = false,
  folderEnumerationError,
  onCheckNow,
  onReconnect,
  onDisconnect,
  onStopHistoricalImport,
  onScheduleChange,
  onExtractEmailBodyPdfChange,
  onEditFolders,
  onFoldersChange,
  className = '',
  labels = {},
}) => {
  const l = { ...defaultLabels, ...labels };
  const relativeTime = lastSync ? formatRelativeTime(lastSync, l) : '';
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [localTime, setLocalTime] = useState(syncScheduleTime || '06:00');
  const [localDay, setLocalDay] = useState(syncScheduleDay ?? 4);
  // #3035 — folder editor
  const [isEditingFolders, setIsEditingFolders] = useState(false);
  const [draftFolders, setDraftFolders] = useState<string[]>([]);
  const [folderError, setFolderError] = useState('');

  const selectedFolders = pollingFolders ?? [];
  // The checkbox list renders the UNION of what the server offers and what is currently
  // polled. A persisted folder the server does not offer (the legacy literal "Sent" on a
  // server whose sent folder is "Gesendete Objekte"; a folder since renamed) must stay
  // visible and untickable: rendering only the offered set made it invisible yet still
  // submitted, so unticking every visible box left a non-empty draft, slipped past the
  // "at least one folder" guard, and saved a poll set that cannot be SELECTed. Dropping it
  // silently instead would change the farmer's selection behind their back.
  const offeredFolders = availableFolders ?? [];
  const renderableFolders = [...offeredFolders, ...draftFolders.filter((f) => !offeredFolders.includes(f))];
  // #3184: decoded label lookup — editor map first (it covers the offered∪persisted
  // union), then the summary map, then the wire name itself. Display only: keys, checked
  // state, drafts and the renderable union above all keep wire identity, because decode
  // is not injective (a raw-UTF-8 server and a mUTF-7 server can display identically).
  // Own-property checks (codex P2): a folder legitimately named `toString`/`__proto__`
  // would otherwise read the INHERITED Object.prototype value from a present-but-empty
  // map — truthy, so the wire-name fallback never fires.
  const displayFolder = (folder: string): string => {
    for (const map of [folderDisplayNames, pollingFolderDisplayNames]) {
      if (map && Object.prototype.hasOwnProperty.call(map, folder)) {
        const value = map[folder];
        if (typeof value === 'string') return value;
      }
    }
    return folder;
  };
  const pollsNothing =
    (statusWarnings ?? []).includes(NO_POLLING_FOLDERS_WARNING) ||
    (pollingFolders !== undefined && selectedFolders.length === 0);
  // #3545: a strictly weaker degradation than `pollsNothing` — the mailbox IS polling,
  // some of its folders just cannot be read. Only the server knows this, so unlike
  // `pollsNothing` there is no client-side fallback to derive it from.
  const hasUnreadableFolders = (statusWarnings ?? []).includes(FOLDERS_UNREADABLE_WARNING);

  const handleEditFolders = () => {
    setFolderError('');
    setDraftFolders(selectedFolders);
    setIsEditingFolders(true);
    // The host fetches the tree here, not on render: each call costs a connect + LOGIN +
    // LIST against the customer's mail server.
    onEditFolders?.(id);
  };

  const handleSaveFolders = () => {
    if (draftFolders.length === 0) {
      setFolderError(l.foldersRequired);
      return;
    }
    setIsEditingFolders(false);
    onFoldersChange?.(id, draftFolders);
  };

  // Render an i18n template with placeholders ({day}, {time}) as React nodes
  const renderTemplate = (template: string, vars: Record<string, React.ReactNode>): React.ReactNode[] =>
    template.split(/(\{[a-z]+\})/g).map((part, i) => {
      const m = part.match(/^\{([a-z]+)\}$/);
      return <React.Fragment key={i}>{m ? (vars[m[1]] ?? part) : part}</React.Fragment>;
    });

  const daySelectEl = (
    <select
      className="integration-card__schedule-inline-select"
      value={localDay}
      onChange={(e) => {
        const newDay = Number(e.target.value);
        setLocalDay(newDay);
        onScheduleChange?.(id, 'weekly', localTime, newDay);
      }}
      aria-label={l.scheduleDayLabel}
    >
      {l.scheduleDays.map((dayName, i) => (
        <option key={i} value={i}>{dayName}</option>
      ))}
    </select>
  );

  const timeInputEl = (
    <input
      type="time"
      className="integration-card__schedule-inline-time"
      value={localTime}
      onChange={(e) => {
        const newTime = e.target.value;
        setLocalTime(newTime);
        onScheduleChange?.(id, syncSchedule || 'daily', newTime, localDay);
      }}
      aria-label={l.scheduleTimeLabel}
    />
  );

  const handleStopClick = () => {
    setShowStopConfirm(true);
  };

  const handleStopConfirm = () => {
    setShowStopConfirm(false);
    onStopHistoricalImport?.(id);
  };

  const handleStopCancel = () => {
    setShowStopConfirm(false);
  };

  const getLastCheckResultText = (): string | null => {
    if (lastCheckNewMessages === null || lastCheckNewMessages === undefined) {
      return null;
    }
    if (lastCheckNewMessages === 0) {
      return l.noNewMessages;
    }
    return `${lastCheckNewMessages} ${l.newMessagesReceived}`;
  };

  const getStatusText = (): string => {
    if (status === 'connected') {
      return `${l.lastChecked} ${relativeTime}`;
    }
    if (status === 'error') {
      return `${l.errorPrefix} ${relativeTime}`;
    }
    if (status === 'paused') {
      return l.paused;
    }
    return l.connecting;
  };

  // #3517 (release-gate review): never while paused. A parked mailbox rendering
  // "Pulled X of Y" beside a live **Stop import** button is worse than merely confusing —
  // that button calls `stop-historical-import`, which REMOVES the batch cap and drops the
  // row onto the uncapped normal-poll path (API.md says so explicitly). A farmer clicking
  // it while parked therefore re-arms the faster path for the instant the operator
  // resumes, which is the 2026-08-17 incident's accelerant, reached from the one screen
  // that is telling them collection is stopped.
  const isHistoricalImportActive =
    status !== 'paused' && historicalImport?.inProgress && historicalImport.total > 0;

  return (
    <div className={`integration-card ${className}`}>
      <div className="integration-card__content">
        <div className="integration-card__icon">
          {getIntegrationIcon(type)}
        </div>
        <div className="integration-card__details">
          <div className="integration-card__header">
            <div className="integration-card__info">
              <h3 className="integration-card__name">{name}</h3>
              <p className="integration-card__account">{account}</p>
            </div>
            <div className="integration-card__status">
              <StatusBadge
                variant={status as StatusBadgeVariant}
                text={getStatusText()}
              />
              {status === 'connected' && getLastCheckResultText() && !isHistoricalImportActive && (
                <span className="integration-card__check-result">
                  {getLastCheckResultText()}
                </span>
              )}
              {isHistoricalImportActive && (
                <div className="integration-card__historical-import">
                  <div className="integration-card__historical-import-status">
                    <Play size={12} className="integration-card__historical-import-icon" />
                    <span>
                      {l.historicalImportPulled} {historicalImport.pulled} {l.historicalImportOf} {historicalImport.total}
                    </span>
                  </div>
                  {showStopConfirm ? (
                    <div className="integration-card__historical-import-confirm">
                      <span className="integration-card__historical-import-confirm-text">
                        {l.stopImportConfirm}
                      </span>
                      <button
                        type="button"
                        className="integration-card__historical-import-confirm-yes"
                        onClick={handleStopConfirm}
                      >
                        {l.stopImportYes}
                      </button>
                      <button
                        type="button"
                        className="integration-card__historical-import-confirm-no"
                        onClick={handleStopCancel}
                      >
                        {l.stopImportNo}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="integration-card__historical-import-stop"
                      onClick={handleStopClick}
                      title={l.stopImport}
                    >
                      <Square size={10} />
                      {l.stopImport}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {status === 'error' && error && (
            <div className="integration-card__error">
              <AlertCircle size={16} className="integration-card__error-icon" />
              <span>{error} {l.notCollecting}</span>
            </div>
          )}

          {/* #3517: a paused mailbox says so in words as well as in a badge. The whole
              point of the state is that the farmer can tell "parked on purpose" from
              "broken"; a badge alone leaves them guessing, and the guess they made during
              the incident was to disconnect and reconnect. */}
          {status === 'paused' && (
            <div className="integration-card__paused">
              <PauseCircle size={16} className="integration-card__paused-icon" />
              <span>{l.pausedExplanation}</span>
            </div>
          )}

          {/* Sync Schedule Controls */}
          {status !== 'error' && onScheduleChange && (
            <div className="integration-card__schedule">
              <span className="integration-card__schedule-label">{l.scheduleLabel}</span>
              <div className="integration-card__schedule-modes">
                {(['automatic', 'daily', 'weekly'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    className={`integration-card__schedule-mode${syncSchedule === mode ? ' integration-card__schedule-mode--active' : ''}`}
                    onClick={() => {
                      onScheduleChange(id, mode, localTime, localDay);
                    }}
                  >
                    {mode === 'automatic' ? l.scheduleAutomatic : mode === 'daily' ? l.scheduleDaily : l.scheduleWeekly}
                  </button>
                ))}
              </div>
              <p className="integration-card__schedule-sentence">
                {syncSchedule === 'automatic' && l.scheduleAutomaticDesc}
                {syncSchedule === 'daily' &&
                  renderTemplate(l.scheduleDailyDesc, { time: timeInputEl })}
                {syncSchedule === 'weekly' &&
                  renderTemplate(l.scheduleWeeklyDesc, { day: daySelectEl, time: timeInputEl })}
              </p>
            </div>
          )}

          {/* #3035: polled-folder coverage + editor. "Checking 2 of 14 folders" is the
              signal that makes a mis-targeted mailbox self-diagnosing instead of looking
              like a quiet one. */}
          {status !== 'error' && pollingFolders !== undefined && (
            <div className="integration-card__folders">
              {pollsNothing ? (
                <div className="integration-card__folders-warning" role="alert">
                  <AlertCircle size={16} className="integration-card__folders-warning-icon" />
                  <span>{l.foldersNoneWarning}</span>
                </div>
              ) : (
                <>
                  <div className="integration-card__folders-summary">
                    <span className="integration-card__folders-label">{l.foldersLabel}</span>
                    <span className="integration-card__folders-names">
                      {selectedFolders.map(displayFolder).join(', ')}
                    </span>
                  </div>
                  {/* #3545: the selection is intact and being polled, so the summary
                      stays — this rides alongside it rather than replacing it. */}
                  {hasUnreadableFolders && (
                    <div className="integration-card__folders-warning" role="alert">
                      <AlertCircle size={16} className="integration-card__folders-warning-icon" />
                      <span>{l.foldersUnreadableWarning}</span>
                    </div>
                  )}
                </>
              )}

              {!isEditingFolders && onEditFolders && (
                <button
                  type="button"
                  className="integration-card__folders-edit"
                  onClick={handleEditFolders}
                >
                  {l.foldersEdit}
                </button>
              )}

              {isEditingFolders && (
                <div className="integration-card__folders-editor">
                  {isLoadingFolders && (
                    <p className="integration-card__folders-loading">{l.foldersLoading}</p>
                  )}

                  {!isLoadingFolders && folderEnumerationError && (
                    <p className="integration-card__folders-error" role="alert">
                      {folderEnumerationError}
                    </p>
                  )}

                  {!isLoadingFolders && !folderEnumerationError && renderableFolders.length > 0 && (
                    <>
                      <span className="integration-card__folders-coverage">
                        {l.foldersCoverage
                          .replace('{count}', String(draftFolders.length))
                          .replace('{total}', String(renderableFolders.length))}
                      </span>
                      <ul className="integration-card__folders-list" aria-label={l.foldersLabel}>
                        {renderableFolders.map((folder) => (
                          <li key={folder} className="integration-card__folders-item">
                            <Checkbox
                              checked={draftFolders.includes(folder)}
                              onChange={(checked) => {
                                setFolderError('');
                                setDraftFolders((current) =>
                                  checked
                                    ? [...current, folder]
                                    : current.filter((f) => f !== folder)
                                );
                              }}
                              // #3184: decoded label, wire value — checked/draft state
                              // and the saved selection stay on wire names.
                              label={displayFolder(folder)}
                              ariaLabel={displayFolder(folder)}
                            />
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  {folderError && (
                    <p className="integration-card__folders-error" role="alert">
                      {folderError}
                    </p>
                  )}

                  <div className="integration-card__folders-actions">
                    <button
                      type="button"
                      className="integration-card__folders-save"
                      onClick={handleSaveFolders}
                      // An editor with nothing to show cannot produce an informed selection.
                      // That state is reachable without an error: opening a second card's
                      // editor hands this one `availableFolders === undefined` (the payload
                      // slot is page-level, "open" is per-card), and Save would then PATCH a
                      // stale draft the farmer can no longer see.
                      disabled={isLoadingFolders || !!folderEnumerationError || renderableFolders.length === 0}
                    >
                      {l.foldersSave}
                    </button>
                    <button
                      type="button"
                      className="integration-card__folders-cancel"
                      onClick={() => {
                        setIsEditingFolders(false);
                        setFolderError('');
                      }}
                    >
                      {l.foldersCancel}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* #1570: Store email body as a separate PDF toggle */}
          {status !== 'error' && onExtractEmailBodyPdfChange && (
            <div className="integration-card__settings">
              <Checkbox
                checked={extractEmailBodyPdf}
                onChange={(checked) => onExtractEmailBodyPdfChange(id, checked)}
                label={l.extractEmailBodyPdf}
                ariaLabel={l.extractEmailBodyPdf}
              />
            </div>
          )}

          <div className="integration-card__actions">
            {status === 'error' ? (
              <button
                type="button"
                className="integration-card__action integration-card__action--reconnect"
                onClick={() => onReconnect?.(id)}
              >
                <RefreshCw size={14} />
                {l.reconnect}
              </button>
            ) : (
              <button
                type="button"
                className={`integration-card__action integration-card__action--check${isChecking ? ' integration-card__action--checking' : ''}`}
                onClick={() => onCheckNow?.(id)}
                /* #3517: the server refuses a manual check on a paused mailbox (400), so
                   offering the button would only produce an error toast for a state that
                   is working as intended. */
                disabled={status === 'connecting' || status === 'paused' || isChecking}
              >
                <RefreshCw size={14} className={isChecking ? 'integration-card__spin' : ''} />
                {isChecking ? l.checking : l.checkNow}
              </button>
            )}
            <button
              type="button"
              className="integration-card__action integration-card__action--disconnect"
              onClick={() => onDisconnect?.(id)}
            >
              <Trash2 size={14} />
              {l.disconnect}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationCard;
